import type { ConsentLanguage } from '../hooks/useConsentLanguage'
import type { ConsentConfigItem, ConsentConfigResponse, ConsentContext, ConsentDocumentContent } from './types'

export function interpolate(
  template: string,
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return template.replace(/\{patient_first_name\}/g, firstName ?? '').replace(/\{patient_last_name\}/g, lastName ?? '')
}

/** Backend requires sectionTitle; JSON.stringify drops undefined keys - avoid silent 422s if config is partial. */
const DEFAULT_SECONDARY_SECTION_TITLE = 'Secondary Consents'

export function resolveSecondarySectionTitle(sectionTitle: string | undefined): string {
  const trimmed = sectionTitle?.trim()
  return trimmed ? trimmed : DEFAULT_SECONDARY_SECTION_TITLE
}

export function normalizePath(pathname: string) {
  return pathname.replace(/\/+$/, '') || '/'
}

export function formatSignedAt(value: string | null | undefined): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return value
  }
}

export function getSecondaryItems(config: ConsentConfigResponse) {
  return config.secondaryItems
}

export function getSecondaryTitle(item: ConsentConfigItem): string {
  return item.title
}

type VariantScenarioKey = 'comparator:default' | 'guardian:proband_only' | 'guardian:duo' | 'guardian:trio'

const titleMap: Record<VariantScenarioKey, (lang: ConsentLanguage, ctx: ConsentContext) => string> = {
  'comparator:default': (lang) => lang.comparatorTitle ?? lang.primaryGuardianCheckboxText,
  'guardian:proband_only': (lang, ctx) =>
    interpolate(
      lang.guardianProbandOnlyTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
  'guardian:duo': (lang, ctx) =>
    interpolate(
      lang.guardianDuoTrioTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
  'guardian:trio': (lang, ctx) =>
    interpolate(
      lang.guardianDuoTrioTitle ?? lang.primaryGuardianCheckboxText,
      ctx.patientFirstName,
      ctx.patientLastName
    ),
}

export function getPrimaryCheckboxText(context: ConsentContext | null, language: ConsentLanguage): string {
  if (!context) return language.primaryGuardianCheckboxText

  const variant = context.primaryConsentCheckboxVariant
  const scenario = context.primaryConsentScenario

  let key: VariantScenarioKey | undefined

  if (variant === 'comparator') {
    key = 'comparator:default'
  } else if (variant === 'guardian' && scenario === 'proband_only') {
    key = 'guardian:proband_only'
  } else if (variant === 'guardian' && scenario === 'duo') {
    key = 'guardian:duo'
  } else if (variant === 'guardian' && scenario === 'trio') {
    key = 'guardian:trio'
  }

  if (key) return titleMap[key](language, context)
  return language.primaryGuardianCheckboxText
}

/** Exact text shown on the primary consent screen, for the consent_event ledger (FAM-108 / G3). */
export function buildPrimaryDocumentContent(
  context: ConsentContext | null,
  language: ConsentLanguage,
  locale: string
): ConsentDocumentContent {
  return {
    locale,
    title: getPrimaryCheckboxText(context, language),
    paragraphs: language.primaryConsentParagraphs,
  }
}

/** Exact text shown for a secondary consent item, for the consent_event ledger (FAM-108 / G3). */
export function buildSecondaryDocumentContent(item: ConsentConfigItem, locale: string): ConsentDocumentContent {
  return {
    locale,
    title: getSecondaryTitle(item),
    paragraphs: item.descriptionParagraphs?.length ? item.descriptionParagraphs : [item.description || item.title],
  }
}
